import {useReducer} from "react";
import reducer from "./reducer";
import AchievementContext, {internalState} from "./context";
import {execute} from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import {IAchievement} from "../../models/achievement/response";
import {ICreateAchievement, IUpdateAchievement} from "../../models/achievement/request";
import {IAchievementQuery} from "../../models/achievement/query";

export interface IProps {
    children: React.ReactNode;
}

const AchievementContextProvider: React.FC<IProps> = (props) => {
    const [state, dispatch] = useReducer(reducer, internalState)

    const getData = async () => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
                const { data } = await EndPoints.achievement.getAllAchievements(state.query);
                dispatch({ type: "SET_LIST", payload: { list: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
            },
            throwException: false,
        });
    };

    const getDetails = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
                const { data } = await EndPoints.achievement.getAchievement(id);
                dispatch({ type: "SET_DETAILS", payload: { details: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
            },
            throwException: false,
        });
    };

    const setDetails = async (data?: IAchievement) => {
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
    };

    const createAchievement = async (request: ICreateAchievement) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
                await EndPoints.achievement.createAchievement(request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
            },
            throwException: true,
        });
    };

    const updateAchievement = async (id: number, request: IUpdateAchievement) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
                await EndPoints.achievement.updateAchievement(id, request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
            },
            throwException: true,
        });
    };

    const deleteAchievement = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
                await EndPoints.achievement.deleteAchievement(id);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
            },
            throwException: true,
        });
    };

    const setSearch = (search?: string) => {
        dispatch({ type: "SET_SEARCH", payload: { search } });
    };

    const setQuery = (query: IAchievementQuery) => {
        dispatch({ type: "SET_QUERY", payload: { query } });
    };

    return (
        <AchievementContext.Provider
            value={{
                ...state,
                actions: {
                    getData,
                    getDetails,
                    setDetails,
                    createAchievement,
                    updateAchievement,
                    deleteAchievement,
                    setSearch,
                    setQuery,
                }
            }}
        >
            {props.children}
        </AchievementContext.Provider>
    )
}

export default AchievementContextProvider;